import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    HostListener,
    inject,
    input,
    signal,
    untracked,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { SeSearchResultsComponent } from "../tile.search.results.component";
import { IPaginatedData, PaginatedList } from "../paginated.list";
import { PaginatedLoadAnnouncer } from "../paginated.load.announcer";
import { convertEventDocumentToTileItem, convertOrganizationDocumentToTileItem } from "../tile.item.converters";
import { TileItem } from "../item.tile.component";
import { DEFAULT_PAGE_SIZE, SeSearchService } from "../se.search.service";
import { S25Util } from "../../../../util/s25-util";
import { S25IconComponent } from "../../../s25-icon/s25.icon.component";
import { s25Resource } from "../../../../util/s25.resource";
import { Item } from "../../../../pojo/Item";
import { Event } from "../../../../pojo/Event";
import {
    DEFAULT_SEARCH_FILTER,
    ObjectSearchFilter,
    SeObjectSearchFilterComponent,
} from "./se.object.search.filter.component";
import { SeSearchUtil } from "./se.object.search.util";
import { S25Icons } from "../../../s25-icon/s25.icon.const";
import { Organization } from "../../../../pojo/Organization";
import Ids = Item.Ids;
import { SeObjectSearchStore } from "./se.object.search.store";
import { S25SidebarComponent } from "../../../s25-sidebar/s25.sidebar.component";
import { SidebarStore } from "../../../s25-sidebar/s25.sidebar.store";
import { S25EmailComponent } from "../../../s25-email/s25.email.component";
import { ContactRole, OrganizationService, WSOrganization } from "../../../../services/organization.service";
import { DataAccess } from "../../../../dataaccess/data.access";
import { StudentEngagementOrg } from "../../StudentEngagementOrg";
import { EventSearchExportOptions } from "./se.event.search.export.service";
import { SeSecurityStore } from "../../se.security.store";
import { RoleRights } from "../../securityConfig/role.rights";
import { AccessLevels } from "../../../../pojo/Fls";
import {
    connect25TemplateOrgIdsParamKey,
    normalizeConnect25TemplateOrgIds,
} from "../../email/connect25.email.template.compose.util";

@TypeManagerDecorator("s25-ng-se-object-search")
@Component({
    selector: "s25-ng-se-object-search",
    template: `
        <div class="object-search-container">
            <header class="search-header">
                <div class="header-left">
                    <h1 class="page-title">{{ pageTitle() }}</h1>
                    @if (totalCount() > 0) {
                        <span class="item-count">{{ totalCount() }} total</span>
                    }
                </div>
            </header>

            <s25-ng-se-object-search-filter
                [itemTypeId]="itemTypeId()"
                [organizations]="organizations()"
                [filterOptions]="filterState()"
                (filterOptionsChange)="filterState.set($event)"
                (searchClick)="runSearch()"
            ></s25-ng-se-object-search-filter>

            <div class="visually-hidden" aria-live="polite" aria-atomic="true">
                {{ liveAnnouncement() }}
            </div>

            <section class="results-region" [attr.aria-busy]="loading()" aria-label="Search results">
                @if (loading()) {
                    <div class="loading-container">
                        <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                        <span class="visually-hidden">Loading {{ itemTypeName() }}...</span>
                    </div>
                } @else if (error()) {
                    <div class="message-container error" role="alert">
                        <s25-ng-icon [type]="'alertCircle'" [size]="24" [ariaLabel]="'Error'" />
                        <div>
                            <p class="message-title">Unable to load {{ itemTypeName() }}</p>
                            <p class="message-text">{{ error() }}</p>
                        </div>
                        <button class="text-btn" (click)="reset()" type="button">Try again</button>
                    </div>
                } @else if (paginatedList()) {
                    @if (paginatedList()!.totalCount() === 0) {
                        <div class="message-container">
                            <s25-ng-icon [type]="iconType()" [size]="32" [ariaLabel]="false" />
                            <div>
                                <p class="message-title">No {{ itemTypeName() }} found</p>
                            </div>
                        </div>
                    } @else {
                        <div class="items-content">
                            <div class="results-toolbar">
                                <p class="results-count">
                                    Showing {{ paginatedList()!.items().length }} of {{ paginatedList()!.totalCount() }}
                                    {{ itemTypeName() }}
                                </p>
                            </div>
                            <s25-ng-se-search-results
                                [paginatedData]="announcingList()!"
                                [searchType]="itemTypeName()!"
                                [eventExport]="eventExportConfig()"
                                [noResultsMessage]="'No ' + itemTypeName() + ' match your criteria'"
                                (refresh)="refresh()"
                                (emailSelected)="emailSelectedOrgs($event)"
                            ></s25-ng-se-search-results>
                        </div>
                    }
                } @else if (!searchParams.value()) {
                    <div class="message-container">
                        <s25-ng-icon [type]="iconType()" [size]="32" [ariaLabel]="false" />
                        <div>
                            <p class="message-title">Apply filters to search {{ itemTypeName() }}</p>
                        </div>
                    </div>
                }
            </section>
        </div>

        @if (isAdmin() && itemTypeId() === OrgTypeId) {
            <s25-ng-sidebar [title]="sidebarTitle()" [mode]="'overlay'" [width]="600">
                @if (isLoadingEmailMembers()) {
                    <s25-ng-loading-inline-static />
                } @else if (sidebarStore.isOpen()) {
                    @if (selectedOrgNames().length) {
                        <p class="sidebar-recipients">
                            Selected: <strong>{{ selectedOrgNames().join(", ") }}</strong>
                        </p>
                    }
                    <s25-ng-email
                        [contextId]="emailContextId()"
                        [allowCustomFrom]="true"
                        [itemTypeId]="emailItemTypeId()"
                        [orgMembers]="bulkEmailMembers()"
                        [initialToRecipients]="initialToRecipients()"
                        [showFormAssignment]="true"
                        [connect25TemplateOrgIds]="connect25TemplateOrgIdsForSidebar()"
                        (closed)="closeEmailSidebar()"
                    />
                }
            </s25-ng-sidebar>
        }
    `,
    styles: `
        .object-search-container {
            max-width: 1400px;
            margin: 0 auto;
        }

        .search-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--color--border, rgba(0, 0, 0, 0.15));
        }

        .header-left {
            display: flex;
            align-items: baseline;
            gap: 1rem;
        }

        .page-title {
            font-size: 1.75rem;
            font-weight: 600;
            color: var(--color--text-strong, rgba(0, 0, 0, 0.9));
            margin: 0;
        }

        .item-count {
            font-size: 0.875rem;
            color: var(--color--text-muted, #666);
            font-weight: 400;
        }

        .results-toolbar {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1rem;
        }

        .results-count {
            margin: 0;
            color: var(--color--text-muted, #666);
            font-size: 0.875rem;
        }

        .text-btn {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            font-size: 0.875rem;
            cursor: pointer;
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            transition: all var(--transition-medium, 200ms) ease;
        }

        .text-btn:hover {
            color: var(--color--text, #333);
            background: var(--color--surface-2-bg, #f0f0f0);
        }

        .text-btn:focus {
            outline: none;
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .loading-container {
            display: flex;
            justify-content: center;
            padding: 3rem;
        }

        .message-container {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 2rem;
            background: var(--color--surface-2-bg, #f0f0f0);
            border-radius: 8px;
            margin-top: 2rem;
        }

        .message-container.error {
            background: var(--color--surface-bg-error, #ffd4d4);
        }

        ::ng-deep .message-container > s25-ng-icon:first-child svg {
            color: var(--color--text-muted, #666);
            flex-shrink: 0;
        }

        ::ng-deep .message-container.error > s25-ng-icon:first-child svg {
            color: var(--color--danger, #b00303);
        }

        .message-container > div {
            flex: 1;
        }

        .message-title {
            font-weight: 500;
            color: var(--color--text-strong, rgba(0, 0, 0, 0.9));
            margin: 0 0 0.25rem 0;
        }

        .message-text {
            color: var(--color--text-muted, #666);
            font-size: 0.875rem;
            margin: 0;
        }

        .message-container.error .message-title {
            color: var(--color--danger, #b00303);
        }

        .message-container.error .message-text {
            color: var(--color--danger, #b00303);
        }

        @media (max-width: 768px) {
            .search-header {
                flex-direction: column;
                align-items: flex-start;
                gap: 1rem;
            }
        }

        :host ::ng-deep .email-component {
            padding: 0 !important;
        }

        :host ::ng-deep .sidebar .sidebar-content {
            padding-top: 0 !important;
        }

        .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineModule,
        SeSearchResultsComponent,
        S25IconComponent,
        SeObjectSearchFilterComponent,
        S25SidebarComponent,
        S25EmailComponent,
    ],
    providers: [SeObjectSearchStore, SidebarStore],
})
export class SeObjectSearchComponent {
    itemTypeId = input.required<Ids>();

    private connect25TemplateOrgIdsRequestId = 0;

    private readonly searchStore = inject(SeObjectSearchStore);
    private readonly securityStore = inject(SeSecurityStore);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly sidebarStore = inject(SidebarStore);
    protected readonly Item = Item;

    readonly contactIdParam = computed(() => this.searchStore.contactIdParam());
    readonly isAdmin = computed(() => this.searchStore.isAdmin());
    readonly emailOrg = computed(() => this.searchStore.emailOrg());
    protected readonly OrgTypeId = Item.Ids.Organization;

    readonly bulkEmailMembers = signal<ContactRole[]>([]);
    readonly initialToRecipients = signal<ContactRole[]>([]);
    readonly isLoadingEmailMembers = signal(false);
    readonly selectedOrgNames = signal<string[]>([]);
    readonly emailSidebarOrgIds = signal<number[]>([]);
    /** One selected org is filed on that organization. Several orgs use the generic mail endpoint. */
    readonly emailContextId = computed(() => {
        const orgIds = this.emailSidebarOrgIds();
        return orgIds.length === 1 ? orgIds[0] : 0;
    });
    readonly emailItemTypeId = computed(() =>
        this.emailSidebarOrgIds().length === 1 ? Item.Ids.Organization : undefined,
    );
    readonly connect25TemplateOrgIdsForSidebar = signal<number[]>([]);

    readonly eventExportConfig = computed((): EventSearchExportOptions | undefined => {
        if (this.itemTypeId() !== Item.Ids.Event) {
            return undefined;
        }
        return {
            filter: this.filterState(),
            userContactId: this.contactIdParam(),
        };
    });

    readonly sidebarTitle = computed(() => this.emailOrg()?.itemName ?? "Email Organizations");

    filterState = signal<ObjectSearchFilter>({ ...DEFAULT_SEARCH_FILTER });

    constructor() {
        effect(() => {
            this.filterState();
            this.reset();
        });

        effect(() => {
            const org = this.emailOrg();
            untracked(() => {
                if (org) void this.openEmailSidebar([org.itemId], [org.itemName ?? ""], org.primaryContact);
            });
        });

        effect(() => {
            const ids = normalizeConnect25TemplateOrgIds(this.emailSidebarOrgIds());
            if (!ids.length) {
                untracked(() => this.connect25TemplateOrgIdsForSidebar.set([]));
                return;
            }
            const requestKey = connect25TemplateOrgIdsParamKey(ids);
            const requestId = ++this.connect25TemplateOrgIdsRequestId;
            void this.securityStore
                .hasAccessForAny([RoleRights.RoleRightsList.SE_ORG_EMAIL], ids, AccessLevels.Read)
                .then((accessByOrgId) => {
                    if (requestId !== this.connect25TemplateOrgIdsRequestId) {
                        return;
                    }
                    const currentKey = connect25TemplateOrgIdsParamKey(this.emailSidebarOrgIds());
                    if (currentKey !== requestKey) {
                        return;
                    }
                    this.connect25TemplateOrgIdsForSidebar.set(ids.filter((orgId) => accessByOrgId[orgId]));
                })
                .catch(() => {
                    if (requestId !== this.connect25TemplateOrgIdsRequestId) {
                        return;
                    }
                    const currentKey = connect25TemplateOrgIdsParamKey(this.emailSidebarOrgIds());
                    if (currentKey !== requestKey) {
                        return;
                    }
                    this.connect25TemplateOrgIdsForSidebar.set([]);
                });
        });

        this.destroyRef.onDestroy(() => this.cleanup());
    }

    @HostListener("window:beforeunload")
    cleanup() {
        if (this.staleQueryId) this.deleteTempSearch(this.staleQueryId, true);
    }

    protected readonly pageTitle = computed(() => {
        const prefix = this.contactIdParam() ? "My " : "",
            suffix = this.isAdmin() ? " Admin" : "";
        return `${prefix}${this.formattedItemTypeName()}${suffix}`;
    });
    protected readonly itemTypeName = computed(() => {
        switch (this.itemTypeId()) {
            case Item.Ids.Event:
                return "events";
            case Item.Ids.Organization:
                return "organizations";
        }
    });
    protected readonly formattedItemTypeName = computed(() => {
        const itemTypeName = this.itemTypeName();
        if (!itemTypeName) return "";
        return `${itemTypeName[0].toUpperCase() + itemTypeName.slice(1)}`;
    });
    protected readonly iconType = computed<S25Icons.Name>(() => {
        switch (this.itemTypeId()) {
            case Item.Ids.Event:
                return "calendarDate";
            case Item.Ids.Organization:
            default:
                return "organization";
        }
    });

    protected readonly error = computed(
        () => this.orgsResource.resource.error()?.message ?? this.eventsResource.resource.error()?.message,
    );
    protected readonly totalCount = computed(() => this.paginatedList()?.totalCount() || 0);

    protected readonly liveAnnouncement = computed(() => {
        if (this.loading()) return `Loading ${this.itemTypeName()}...`;
        if (this.error()) return `Error: ${this.error()}`;
        const count = this.totalCount();
        if (count === 0) return `No ${this.itemTypeName()} found`;
        return `Found ${count} ${this.itemTypeName()}`;
    });

    protected readonly loading = computed(
        () =>
            this.searchParams.resource.isLoading() ||
            this.orgsResource.resource.isLoading() ||
            this.eventsResource.resource.isLoading(),
    );

    private orgsListCache: PaginatedList<TileItem> | null = null;
    protected organizations = computed(() => this.orgsResource.value() ?? []);

    private readonly isMyEvents = computed(() => this.searchStore.isMyEvents());

    readonly searchParams = s25Resource({
        params: () => this.filterState(),
        loader: () =>
            SeSearchUtil.buildSearchParams(this.filterState(), this.itemTypeId(), {
                myOrgs: this.searchStore.isMyOrgs() || undefined,
                isAdmin: this.isAdmin(),
            })!,
    });

    private staleQueryId: number;

    private orgsResource = s25Resource({
        if: () => !this.isMyEvents() || this.itemTypeId() !== Item.Ids.Event,
        params: () => this.searchParams.value(),
        loader: async ({ params }) => {
            if (params) {
                const [orgs, error] = await S25Util.Maybe(
                    SeSearchService.getOrganizations(<Organization.WsQueryParams>params, {
                        page: 1,
                        page_size: DEFAULT_PAGE_SIZE,
                        paginate: "T",
                    }),
                );
                if (error) {
                    this.orgsListCache?.reset();
                    throw error;
                }

                if (params.query_id && params.query_id !== this.staleQueryId) {
                    if (this.staleQueryId) this.deleteTempSearch(this.staleQueryId);
                    this.staleQueryId = params.query_id;
                }

                const isOrgType = this.itemTypeId() === Item.Ids.Organization;
                if (isOrgType && !this.orgsListCache) {
                    this.orgsListCache = new PaginatedList(convertOrganizationDocumentToTileItem, (items) => items);
                    this.orgsListCache.configureFetch(
                        (page, pageSize, key) =>
                            SeSearchService.getOrganizations(<Organization.WsQueryParams>params, {
                                page,
                                page_size: pageSize,
                                paginate: key,
                            }),
                        DEFAULT_PAGE_SIZE,
                    );
                }
                if (isOrgType) this.orgsListCache?.setInitialData(orgs);

                return orgs.items ?? [];
            }
        },
    });

    private eventsListCache: PaginatedList<TileItem> | null = null;
    private eventsResource = s25Resource({
        if: () => this.itemTypeId() === Item.Ids.Event,
        params: () => {
            const searchParams = this.searchParams.value();
            if (!searchParams) {
                return undefined;
            }
            return { searchParams };
        },
        loader: async ({ params }) => {
            const searchParams = <Event.WsQueryParams>params.searchParams;
            if (searchParams) {
                const filter = this.filterState();
                if (filter?.timeRange === "custom" && (!searchParams.start_dt || !searchParams.end_dt)) return;
                if (this.isMyEvents() && !searchParams.attendee_state?.length && searchParams.attending !== "T") {
                    Object.assign(searchParams, SeSearchService.myEventsQueryParams());
                }

                if (!this.eventsListCache) {
                    this.eventsListCache = new PaginatedList(convertEventDocumentToTileItem, (items) => items);
                    this.eventsListCache.configureFetch(
                        (page, pageSize, key) =>
                            SeSearchService.searchEvents(searchParams, {
                                page,
                                page_size: pageSize,
                                paginate: key,
                            }),
                        DEFAULT_PAGE_SIZE,
                    );
                }
                const [events, eventsError] = await S25Util.Maybe(
                    SeSearchService.searchEvents(searchParams, {
                        page: 1,
                        page_size: DEFAULT_PAGE_SIZE,
                        paginate: "T",
                    }),
                );
                if (eventsError) {
                    this.eventsListCache?.reset();
                    throw eventsError;
                }

                this.eventsListCache?.setInitialData(events);
                return events;
            }
        },
    });

    protected paginatedList = computed<PaginatedList<TileItem> | null>(() => {
        switch (this.itemTypeId()) {
            case Item.Ids.Event:
                const eventsData = this.eventsResource.value();
                if (eventsData && this.eventsListCache) return this.eventsListCache;
                return null;
            case Item.Ids.Organization:
                const orgsData = this.orgsResource.value();
                if (orgsData && this.orgsListCache) return this.orgsListCache;
                return null;
            default:
                return null;
        }
    });

    protected readonly announcingList = computed<IPaginatedData<TileItem> | null>(() => {
        const list = this.paginatedList();
        return (
            list && PaginatedLoadAnnouncer.withLoadAnnouncements(list, () => this.itemTypeName()!, DEFAULT_PAGE_SIZE)
        );
    });

    runSearch(): void {
        this.reset();
    }

    reset() {
        switch (this.itemTypeId()) {
            case Item.Ids.Event:
                this.eventsListCache = null;
                break;
            case Item.Ids.Organization:
                this.orgsListCache = null;
                break;
        }
        this.refresh();
    }

    refresh() {
        switch (this.itemTypeId()) {
            case Item.Ids.Event:
                return this.eventsResource.resource.reload();
            case Item.Ids.Organization:
                return this.orgsResource.resource.reload();
        }
    }

    deleteTempSearch(id: number, useKeepalive = false) {
        if (!id) return;
        if (useKeepalive) {
            void fetch(DataAccess.composeUrl(`/se/org_search.json?query_id=${id}`), {
                method: "DELETE",
                keepalive: true,
            });
        } else {
            void SeSearchService.deleteSearch(id);
        }
    }

    async openEmailSidebar(ids: number[], names: string[], primaryContact?: StudentEngagementOrg.OrgContact) {
        this.emailSidebarOrgIds.set(ids);
        if (!this.sidebarStore.isOpen()) this.sidebarStore.toggle();
        this.isLoadingEmailMembers.set(true);
        const [members, err] = await S25Util.Maybe(OrganizationService.getContactsForOrgs(ids));
        this.isLoadingEmailMembers.set(false);
        if (err) return S25Util.showError(err);
        // Set names and members together — never mismatched in the UI
        this.selectedOrgNames.set(names);
        this.bulkEmailMembers.set(members ?? []);

        if (primaryContact?.itemId) {
            this.initialToRecipients.set([
                {
                    itemId: Number(primaryContact.itemId),
                    itemName: primaryContact.itemName ?? "",
                    itemEmail: primaryContact.itemEmail,
                    isPrimary: true,
                    role: { itemId: 0, itemName: "Primary Contact" },
                    itemTypeId: Item.Ids.Contact,
                },
            ]);
        } else {
            this.initialToRecipients.set([]);
        }
    }

    async emailSelectedOrgs(ids: number[]) {
        const nameMap = new Map(this.organizations().map((o) => [o.itemId, o.itemName as string]));
        const missingIds = ids.filter((id) => !nameMap.has(id));
        if (missingIds.length) {
            const [orgs] = await S25Util.Maybe(OrganizationService.getOrganizationsById(missingIds));
            orgs?.forEach((o: WSOrganization) => nameMap.set(Number(o.organization_id), o.organization_name));
        }
        const names = ids.map((id) => nameMap.get(id) ?? `Org #${id}`);
        await this.openEmailSidebar(ids, names);
    }

    closeEmailSidebar() {
        this.sidebarStore.close();
        this.searchStore.closeEmailSidebar();
        this.bulkEmailMembers.set([]);
        this.initialToRecipients.set([]);
        this.selectedOrgNames.set([]);
        this.emailSidebarOrgIds.set([]);
        this.connect25TemplateOrgIdsForSidebar.set([]);
    }
}
